import React, { useState, useEffect } from 'react';
import { ChevronRight, RotateCcw, PlusCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface DrawsTabProps {
  competitionId: string;
  currentUser: any;
  competition: any;
  onViewMatches: (roundNumber: number) => void;
  activeCategory?: string | null;
}

export const DrawsTab: React.FC<DrawsTabProps> = ({ competitionId, currentUser, competition, activeCategory, onViewMatches }) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRoundModal, setShowNewRoundModal] = useState(false);
  const [newRoundMode, setNewRoundMode] = useState('MANUAL');
  const [newRoundDate, setNewRoundDate] = useState('');

  const isManager = currentUser?.role === 'ADMIN' || competition?.managerId === currentUser?.id;
  const isTournament = competition?.type === 'TOURNAMENT';
  const isAutoDraw = isTournament 
    ? competition?.config?.tournamentRules?.matchGenerationMode === 'AUTO_DRAW'
    : competition?.config?.ladderRules?.matchGenerationMode === 'AUTO_DRAW';

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    fetchMatches();
  }, [competitionId]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/competitions/${competitionId}/matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatches(data);
      }
    } catch (e) {
      console.log('Error fetching matches', e);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRound = async () => {
    try {
      setLoading(true);
      if (isAutoDraw) {
        const payload: any = {};
        if (!isTournament) {
          payload.roundEndMode = newRoundMode;
          if (newRoundMode === 'AUTO') {
            payload.roundEndDate = newRoundDate;
          }
        }
        if (activeCategory) {
          payload.category = activeCategory;
        }
        const res = await fetch(`${API_URL}/api/competitions/${competitionId}/matches/auto-draw`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success(isTournament ? 'Chave gerada com sucesso!' : 'Rodada gerada com sucesso!');
          setShowNewRoundModal(false);
          window.location.reload();
        } else {
          const data = await res.json();
          toast.error(data.error || 'Erro ao gerar partidas');
        }
      } else {
        const updatedConfig = { ...competition?.config };
        updatedConfig.currentRoundEndMode = newRoundMode;
        if (newRoundMode === 'AUTO') {
          updatedConfig.currentRoundEndDate = newRoundDate;
        } else {
          delete updatedConfig.currentRoundEndDate;
        }
        updatedConfig.currentRound = maxRound + 1;
        const res = await fetch(`${API_URL}/api/competitions/${competitionId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ config: updatedConfig })
        });
        if (res.ok) {
          toast.success('Rodada iniciada com sucesso (Modo Manual/Desafio)');
          setShowNewRoundModal(false);
          window.location.reload();
        } else {
          const data = await res.json();
          toast.error(data.error || 'Erro ao iniciar rodada');
        }
      }
    } catch (err) {
      toast.error('Erro de conexão ao iniciar rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleEndRound = async () => {
    if (!window.confirm('Isso encerrará a rodada atual. Partidas sem placar receberão W.O. Duplo. Deseja continuar?')) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/competitions/${competitionId}/end-round`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Rodada encerrada com sucesso!');
        fetchMatches();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Erro ao encerrar rodada');
      }
    } catch (err) {
      toast.error('Erro de conexão ao encerrar rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleUndoRound = async () => {
    if (!window.confirm('CUIDADO: Isso excluirá todas as partidas desta etapa. Deseja realmente desfazer a etapa?')) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/competitions/${competitionId}/rounds/undo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Etapa desfeita com sucesso!');
        fetchMatches();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Erro ao desfazer etapa');
      }
    } catch (err) {
      toast.error('Erro de conexão ao desfazer etapa');
    } finally {
      setLoading(false);
    }
  };

  const roundsDict: { [key: number]: any[] } = {};
  const filteredMatches = matches.filter(m => activeCategory ? m.category === activeCategory : (!m.category || m.category === 'Geral'));
  filteredMatches.forEach(m => {
    const r = m.round || 1;
    if (!roundsDict[r]) roundsDict[r] = [];
    roundsDict[r].push(m);
  });
  const roundKeysArray = Object.keys(roundsDict).map(Number);
  if (competition?.config?.currentRound && !roundKeysArray.includes(competition.config.currentRound)) {
    roundKeysArray.push(competition.config.currentRound);
  }
  const roundKeys = roundKeysArray.sort((a, b) => isTournament ? a - b : b - a);
  const maxRound = roundKeys.length > 0 ? Math.max(...roundKeys) : 0;
  
  // For tournaments, the "current" round is the earliest round with pending matches
  const currentRound = isTournament 
    ? roundKeys.find(r => (roundsDict[r] || []).some((m: any) => m.status === 'PENDING')) || maxRound
    : maxRound;

  return (
    <div style={{ padding: '20px' }}>
      {isManager && (
        <div style={{ marginBottom: '24px', display: 'flex', gap: '12px' }}>
          {isTournament && isAutoDraw ? (
            <button 
              onClick={handleStartRound}
              className="mf-btn-primary" 
              style={{ flex: 1, padding: '16px', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px' }}
            >
              <PlusCircle size={20} style={{marginRight: '8px', verticalAlign: 'middle'}}/>
              Gerar Chave Automática
            </button>
          ) : (
            <button 
              className="tt-button primary" 
              style={{ flex: 1, padding: '16px', borderRadius: '12px', fontWeight: 'bold', backgroundColor: '#3A8E58', color: '#FFF', fontSize: '16px' }}
              onClick={() => setShowNewRoundModal(true)}
            >
              <PlusCircle size={20} style={{marginRight: '8px', verticalAlign: 'middle'}}/>
              Iniciar Nova Etapa
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)'}}>
          Carregando sorteios...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {roundKeys.length === 0 && (
            <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.05)', borderRadius: '12px'}}>
              Nenhuma etapa foi iniciada ainda.
            </div>
          )}
            {roundKeys.map(round => {
              const isCurrent = round === currentRound;
              const roundMatches = roundsDict[round] || [];
              const completedCount = roundMatches.filter(m => m.status === 'COMPLETED' || m.status === 'FINISHED').length;
              const totalMatches = roundMatches.length;
              const isFullyCompleted = completedCount === totalMatches && totalMatches > 0;
  
              return (
                <div key={round} style={{ background: 'rgba(255,255,255,0.05)', border: isCurrent ? '1px solid var(--primary-color)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, color: 'white', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isTournament ? (roundMatches[0]?.stage || `Fase ${round}`) : `Rodada / Etapa ${round}`}
                        {isCurrent && <span style={{ fontSize: '12px', background: 'var(--primary-color)', color: 'black', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Atual</span>}
                      </h3>
                      <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                        {completedCount} de {totalMatches} partidas concluídas
                      </p>
                  </div>
                  <button 
                    onClick={() => onViewMatches(round)}
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Ver Partidas <ChevronRight size={16} />
                  </button>
                </div>
                
                {isManager && isCurrent && !isTournament && (
                  <div style={{ padding: '16px', background: 'rgba(0,0,0,0.2)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button 
                      onClick={handleEndRound}
                      disabled={isFullyCompleted}
                      style={{ flex: 1, minWidth: '180px', padding: '12px', borderRadius: '8px', fontWeight: 'bold', background: isFullyCompleted ? 'rgba(255,255,255,0.1)' : '#1976D2', color: isFullyCompleted ? 'rgba(255,255,255,0.4)' : 'white', border: 'none', cursor: isFullyCompleted ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <CheckCircle size={18} />
                      Fechar Etapa (W.O. nas Pendentes)
                    </button>
                    
                    <button 
                      onClick={handleUndoRound}
                      style={{ flex: 1, minWidth: '180px', padding: '12px', borderRadius: '8px', fontWeight: 'bold', background: 'rgba(244, 67, 54, 0.1)', color: '#F44336', border: '1px solid rgba(244, 67, 54, 0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <RotateCcw size={18} />
                      Desfazer Etapa
                    </button>
                  </div>
                )}
                {isManager && isTournament && isCurrent && (
                  <div style={{ padding: '16px', background: 'rgba(0,0,0,0.2)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => {
                        if (isFullyCompleted) return;
                        toast.error("Em torneios, lance os resultados de cada jogo. Partidas futuras dependem dos resultados anteriores.");
                      }}
                      disabled={isFullyCompleted}
                      style={{ flex: 1, minWidth: '180px', padding: '12px', borderRadius: '8px', fontWeight: 'bold', background: isFullyCompleted ? 'rgba(255,255,255,0.1)' : '#1976D2', color: isFullyCompleted ? 'rgba(255,255,255,0.4)' : 'white', border: 'none', cursor: isFullyCompleted ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: 0.5 }}
                    >
                      <CheckCircle size={18} />
                      Fechar Etapa (W.O. nas Pendentes)
                    </button>
                    
                    <button 
                      onClick={handleUndoRound}
                      style={{ flex: 1, minWidth: '180px', padding: '12px', borderRadius: '8px', fontWeight: 'bold', background: 'rgba(244, 67, 54, 0.1)', color: '#F44336', border: '1px solid rgba(244, 67, 54, 0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <RotateCcw size={18} />
                      Zerar Chave / Refazer Sorteio
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNewRoundModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1A112C', width: '100%', maxWidth: '400px', borderRadius: '12px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px', color: 'white' }}>Iniciar Nova Rodada</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Fechamento da Rodada</label>
                <select value={newRoundMode} onChange={e => setNewRoundMode(e.target.value)} style={{ padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}>
                  <option value="MANUAL" style={{ color: 'black' }}>Manual (Gestor encerra)</option>
                  <option value="AUTO" style={{ color: 'black' }}>Automático (Por Data)</option>
                </select>
              </div>
              {newRoundMode === 'AUTO' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Data Limite</label>
                  <input type="date" value={newRoundDate} onChange={e => setNewRoundDate(e.target.value)} style={{ padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', colorScheme: 'dark' }} />
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button onClick={() => setShowNewRoundModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}>Cancelar</button>
                <button onClick={handleStartRound} style={{ flex: 1, padding: '12px', borderRadius: '8px', background: 'var(--primary-color)', color: 'black', border: 'none', fontWeight: 'bold' }}>Confirmar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
